import axios from "axios";
import { currentLocale } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

export const getToken = () =>
  localStorage.getItem("fm_token") || sessionStorage.getItem("fm_token");

export const setToken = (token, remember) => {
  localStorage.removeItem("fm_token");
  sessionStorage.removeItem("fm_token");
  (remember ? localStorage : sessionStorage).setItem("fm_token", token);
};

export const clearToken = () => {
  localStorage.removeItem("fm_token");
  sessionStorage.removeItem("fm_token");
};

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export function apiError(detail) {
  const d = detail?.response?.data?.detail ?? detail;
  if (d == null) return "Något gick fel. Försök igen.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (typeof d?.msg === "string") return d.msg;
  return String(detail?.message || "Något gick fel.");
}

export const ROLE_LABELS = {
  superadmin: "Superadmin",
  company_admin: "Företagsadmin",
  manager: "Chef",
  supervisor: "Arbetsledare",
  employee: "Tekniker",
};

// Egna roller (hämtas från /api/roles) – används för etiketter och behörighet
let ROLES_CACHE = [];

export const setRolesCache = (roles) => {
  ROLES_CACHE = roles || [];
};

export const getRolesCache = () => ROLES_CACHE;

export const loadRoles = async () => {
  const { data } = await api.get("/roles");
  setRolesCache(data);
  return data;
};

export const roleLabel = (role, t) => {
  const custom = ROLES_CACHE.find((r) => r.slug === role);
  if (custom && !custom.builtin) return custom.label;
  return t ? t(`roles.${role}`) : ROLE_LABELS[role] || role;
};

export const isCustomLeaderRole = (role) =>
  ROLES_CACHE.some((r) => r.slug === role && !r.builtin && r.base === "supervisor");

export const dateOf = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString(currentLocale(), { day: "numeric", month: "long", year: "numeric" })
    : "–";

export const STATUS_LABELS = {
  planned: "Planerat",
  in_progress: "Pågår",
  done: "Klart",
  cancelled: "Avbokat",
};

export const ABSENCE_LABELS = {
  vacation: "Semester",
  sick: "Sjukdom",
  vab: "VAB",
  leave: "Ledighet",
  other: "Övrigt",
};

export const fmtDuration = (seconds) => {
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${s} s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h} h ${m} min` : `${m} min`;
};

export const jobTime = (job, userId) => {
  const logs = job?.time_logs || [];
  const total = logs.reduce((s, l) => s + (l.seconds || 0), 0);
  const running = logs.find((l) => l.user_id === userId && !l.end);
  return { total, running };
};

export const ABSENCE_STATUS = {
  pending: "Väntar",
  approved: "Godkänd",
  rejected: "Nekad",
};

export const isSuperAdmin = (role) => role === "superadmin";

export const isLeader = (role) =>
  ["superadmin", "company_admin", "manager", "supervisor"].includes(role) || isCustomLeaderRole(role);
export const isAdmin = (role) => ["superadmin", "company_admin", "manager"].includes(role);

export const WORKFLOW = {
  not_started: { label: "Ej påbörjad", tone: "slate", button: "STARTA RESA", action: "start_travel" },
  travel_out: { label: "Pågående resa", tone: "blue", button: "FRAMME", action: "arrive" },
  on_site: { label: "Framme", tone: "indigo", button: "STARTA JOBB", action: "start_work" },
  working: { label: "Pågående arbete", tone: "green", button: "PAUS", action: "toggle_pause" },
  paused: { label: "Paus", tone: "orange", button: "PAUS – TRYCK FÖR ATT ÅTERUPPTA", action: "toggle_pause" },
  work_done: { label: "Jobb avslutat", tone: "teal", button: "RESA HEM", action: "travel_home" },
  travel_home: { label: "Pågående hemresa", tone: "blue", button: "AVSLUTA", action: "complete" },
  completed: { label: "Avslutad", tone: "slate", button: null, action: null },
  job_paused: { label: "Jobbet pausat", tone: "orange", button: "FORTSÄTT JOBB", action: "resume_job" },
  ready_to_invoice: { label: "Färdig för fakturering", tone: "teal", button: null, action: null },
};

export const clock = (seconds) => {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

export const timeOf = (iso) => (iso ? new Date(iso).toLocaleTimeString(currentLocale(), { hour: "2-digit", minute: "2-digit" }) : "–");
export const dateTimeOf = (iso) =>
  iso ? new Date(iso).toLocaleString(currentLocale(), { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "–";
